import {
  BottomSheet,
  BottomSheetIllustrationContent,
  Span,
  Text,
  useModalState,
} from "@clipboard-health/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Stack } from "@mui/material";
// eslint-disable-next-line no-restricted-imports
import { logout } from "@src/app/store/session/actions";
import FetchOtpButtonInDevelopmentMode from "@src/appV2/Debug/components/FetchOtpButtonInDevelopmentMode";
import { formatUsPhoneNumber, useToast } from "@src/appV2/lib";
import { OtpFieldBoxes } from "@src/appV2/lib/FormFields/OtpField/OtpFieldBoxes";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useCountdownTimer } from "@src/appV2/lib/utils/useCountdownTimer";
import { PROFILE_FULL_PATH } from "@src/appV2/redesign/Accounts/paths";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { isAxiosError } from "axios";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { useRequestProfileOtp } from "../../api/useRequestProfileOtp";
import { useUpdateProfile } from "../../api/useUpdateProfile";
import { useValidatePhoneOtp } from "../../api/useValidatePhoneOtp";
import { ProfileUpdateLocalStorageItems } from "../../constants";
import { otpFormSchema } from "../../types/otpFormSchema";

interface VerifyPhoneOtpStepProps {
  phone: string;
  onPhoneUpdated: () => void;
}

export function VerifyPhoneOtpStep({ phone, onPhoneUpdated }: VerifyPhoneOtpStepProps) {
  const { showErrorToast, showSuccessToast } = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const confirmationModal = useModalState();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { mutateAsync: requestOtp, isLoading: isRequestingOtp } = useRequestProfileOtp();
  const { mutateAsync: validateOtp, isLoading: isVerifyingOtp } = useValidatePhoneOtp();
  const { mutateAsync: updateProfile, isLoading: isUpdatingPhone } = useUpdateProfile();
  const [, setUpdateProfilePhoneNumberChanged] = useLocalStorage(
    ProfileUpdateLocalStorageItems.UPDATE_PROFILE_PHONE_NUMBER_CHANGED,
    ""
  );

  const formMethods = useForm({
    defaultValues: {
      otp: "",
    },
    resolver: zodResolver(otpFormSchema),
    mode: "onChange",
    reValidateMode: "onSubmit",
  });

  const {
    handleSubmit,
    setError,
    clearErrors,
    setValue,
    formState: { isSubmitting, isValid },
  } = formMethods;

  const { countdownIsLoading, startTimer, secondsLeft } = useCountdownTimer({
    durationInSeconds: 25,
  });

  const formattedPhone = formatUsPhoneNumber(phone);

  // Auto-request OTP on mount
  useEffect(() => {
    async function requestInitialOtp() {
      try {
        await requestOtp({
          phone,
        });
        startTimer();
      } catch (error) {
        if (isAxiosError(error)) {
          const message =
            (error.response?.data as { message?: string })?.message ??
            "Failed to send verification code";
          showErrorToast(message);
        } else {
          showErrorToast("Failed to send verification code. Please try again.");
        }
      }
    }

    void requestInitialOtp();
  }, [phone, requestOtp, showErrorToast, startTimer]);

  const handleResendOtp = async () => {
    if (countdownIsLoading) {
      return;
    }

    try {
      await requestOtp({
        phone,
      });
      startTimer();
      showSuccessToast("Verification code sent");
    } catch (error) {
      if (isAxiosError(error)) {
        const message =
          (error.response?.data as { message?: string })?.message ??
          "Failed to resend verification code";
        showErrorToast(message);
      } else {
        showErrorToast("Failed to resend verification code. Please try again.");
      }
    }
  };

  const onSubmit = async (formData: { otp: string }) => {
    // Clear any previous errors before validating
    clearErrors("otp");

    try {
      const response = await validateOtp({
        phone,
        otp: formData.otp,
      });

      // If there's an error in the response, it means OTP is invalid

      if (response.data.error) {
        setError("otp", {
          message: "Invalid verification code. Please try again.",
        });
        showErrorToast("Invalid OTP");
        return;
      }

      // If no error, OTP is valid - show confirmation modal
      confirmationModal.openModal();
    } catch (error) {
      if (isAxiosError(error) && error.response?.data) {
        const responseData = error.response.data as { message?: string; error?: string };
        setError("otp", {
          message: responseData.message ?? "Failed to verify code. Please try again.",
        });
        showErrorToast(responseData.message ?? "Failed to verify code");
      } else {
        setError("otp", {
          message: "Failed to verify code. Please try again.",
        });
        showErrorToast("Failed to verify code. Please try again.");
      }
    }
  };

  const handleConfirmUpdate = async () => {
    try {
      await updateProfile({
        profile: {
          phone,
        },
      });
      setUpdateProfilePhoneNumberChanged("true");

      showSuccessToast("Phone number updated successfully");

      setIsLoggingOut(true);

      dispatch(logout());

      onPhoneUpdated();
    } catch (error) {
      confirmationModal.closeModal();
      if (isAxiosError<{ message?: string; error?: string }>(error) && error.response?.data) {
        const errorMessage =
          error.response.data.message ?? "Failed to update phone number. Please try again.";
        setError("otp", {
          message: errorMessage,
        });
        showErrorToast(errorMessage);
      } else {
        const errorMessage = "Failed to update phone number. Please try again.";
        setError("otp", {
          message: errorMessage,
        });
        showErrorToast(errorMessage);
      }
    }
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <Stack
          ref={formRef}
          component="form"
          sx={{ paddingY: 4, gap: 12 }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Stack spacing={6} marginBottom={12}>
            <Text variant="body2">
              We&apos;ve just sent a verification code to{" "}
              <Span sx={{ whiteSpace: "nowrap" }}>
                <b>{formattedPhone}</b>
              </Span>
              .
            </Text>
            <OtpFieldBoxes name="otp" />
            <FetchOtpButtonInDevelopmentMode
              phoneNumber={phone}
              onSuccess={(otp) => {
                setValue("otp", otp);
              }}
            />
          </Stack>
          <Stack spacing={2} sx={{ marginTop: 12 }}>
            <LoadingButton
              fullWidth
              type="submit"
              variant="contained"
              isLoading={isSubmitting || isVerifyingOtp}
              disabled={!isValid || isRequestingOtp}
            >
              Continue
            </LoadingButton>
            <Button
              fullWidth
              variant="text"
              size="small"
              disabled={countdownIsLoading || isRequestingOtp}
              onClick={handleResendOtp}
            >
              {countdownIsLoading
                ? `Resend code in ${secondsLeft}s`
                : "Didn't receive a code? Resend"}
            </Button>
          </Stack>
        </Stack>
      </FormProvider>
      <BottomSheet modalState={confirmationModal}>
        <BottomSheetIllustrationContent
          illustrationType="mail-person"
          title="You need to log in again"
          description="To validate your new phone number, you will need to log out and log in again. This step is necessary for us to properly configure your account."
        />
        <DialogFooter orientation="vertical" sx={{ marginTop: 10 }}>
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={isUpdatingPhone || isLoggingOut}
            onClick={handleConfirmUpdate}
          >
            Confirm
          </LoadingButton>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => {
              confirmationModal.closeModal();
              history.push(PROFILE_FULL_PATH);
            }}
          >
            Cancel
          </Button>
        </DialogFooter>
      </BottomSheet>
    </>
  );
}
